import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import {
  ConsoleProvider,
  EventPayloadMap,
  EventPriority,
  TrackingEvent,
  TrackingEventType,
  TrackingManager,
  TrackingManagerConfig,
  TrackingMetadata,
  TrackingProvider,
} from '@ds24/tracking-core';

export const TRACKING_CONFIG = new InjectionToken<TrackingManagerConfig>('TRACKING_CONFIG');

@Injectable({
  providedIn: 'root',
})
export class TrackingService implements OnDestroy {
  private readonly manager: TrackingManager;

  constructor(@Optional() @Inject(TRACKING_CONFIG) config: TrackingManagerConfig = {}) {
    this.manager = new TrackingManager(config);
    this.manager.registerProvider(new ConsoleProvider());
  }

  public registerProvider(provider: TrackingProvider): void {
    this.manager.registerProvider(provider);
  }

  public unregisterProvider(providerName: string): void {
    this.manager.unregisterProvider(providerName);
  }

  public addMetadata(metadata: Partial<TrackingMetadata>): void {
    this.manager.addMetadata(metadata);
  }

  public track<T extends TrackingEventType>(
    type: T,
    payload?: EventPayloadMap[T],
    priority: EventPriority = EventPriority.INFO
  ): void {
    this.manager.track(type, payload, priority);
  }

  // Log-level based tracking aliases with overloads for optional payloads
  public error(error: Error): void {
    this.manager.error(error);
  }

  public warn<T extends TrackingEventType>(name: T): void;
  public warn<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public warn<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.manager.warn(name, payload);
  }

  public info<T extends TrackingEventType>(name: T): void;
  public info<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public info<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.manager.info(name, payload);
  }

  public debug<T extends TrackingEventType>(name: T): void;
  public debug<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public debug<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.manager.debug(name, payload);
  }

  public trace<T extends TrackingEventType>(name: T): void;
  public trace<T extends TrackingEventType>(name: T, payload: EventPayloadMap[T]): void;
  public trace<T extends TrackingEventType>(name: T, payload?: EventPayloadMap[T]): void {
    this.manager.trace(name, payload);
  }

  // Event listener methods
  public onEventReceived(callback: (event: TrackingEvent) => void): () => void {
    return this.manager.onEventReceived(callback);
  }

  public offEventReceived(callback: (event: TrackingEvent) => void): void {
    this.manager.offEventReceived(callback);
  }

  public onEventProcessed(callback: (event: TrackingEvent) => void): () => void {
    return this.manager.onEventProcessed(callback);
  }

  public offEventProcessed(callback: (event: TrackingEvent) => void): void {
    this.manager.offEventProcessed(callback);
  }

  ngOnDestroy(): void {
    // Destroy the manager
    this.manager.destroy();
  }
}
